import { GQL } from "@binale-tech/shared";

import { filingCovers } from "@accounting/common/metrics/vatReconciliation";

import { AGENT_PHASES } from "../agentPhases";
import { AgentRecordsNotLoadedError } from "../runner/errors";
import { withThinking } from "../runner/runAgent";
import type {
    AgentBucketChange,
    AgentContext,
    AgentReportData,
    AgentStep,
    AgentStepId,
    AgentStepReport,
    AgentStepStatus,
    AgentTodoAction,
    AgentTodoItem,
    ReportLine,
} from "../runner/types";

const PENDING_KINDS: Partial<Record<GQL.IPendingBucketKind, ReportLine["textId"]>> = {
    [GQL.IPendingBucketKind.DocumentsNeedAnswer]: "app.agent.report.pending.DOCUMENTS_NEED_ANSWER",
    [GQL.IPendingBucketKind.DocumentsNotProcessed]: "app.agent.report.pending.DOCUMENTS_NOT_PROCESSED",
    [GQL.IPendingBucketKind.DocumentsReadyToBook]: "app.agent.report.pending.DOCUMENTS_READY_TO_BOOK",
    [GQL.IPendingBucketKind.BankTransactionsNotBooked]: "app.agent.report.pending.BANK_TRANSACTIONS_NOT_BOOKED",
    [GQL.IPendingBucketKind.KasseTransactionsNotBooked]: "app.agent.report.pending.KASSE_TRANSACTIONS_NOT_BOOKED",
    [GQL.IPendingBucketKind.AiDraftsToReview]: "app.agent.report.pending.AI_DRAFTS_TO_REVIEW",
    [GQL.IPendingBucketKind.DraftsWithoutAccount]: "app.agent.report.pending.DRAFTS_WITHOUT_ACCOUNT",
};

const sumByKind = (buckets: GQL.IPendingBucket[]): Map<GQL.IPendingBucketKind, number> => {
    const totals = new Map<GQL.IPendingBucketKind, number>();
    for (const b of buckets) {
        totals.set(b.kind, (totals.get(b.kind) ?? 0) + b.count);
    }
    return totals;
};

const BUCKET_ACTIONS: Partial<Record<GQL.IPendingBucketKind, AgentTodoAction>> = {
    [GQL.IPendingBucketKind.DocumentsNeedAnswer]: "openDocuments",
    [GQL.IPendingBucketKind.DocumentsNotProcessed]: "openDocuments",
    [GQL.IPendingBucketKind.DocumentsReadyToBook]: "openDocuments",
    [GQL.IPendingBucketKind.BankTransactionsNotBooked]: "openBank",
    [GQL.IPendingBucketKind.KasseTransactionsNotBooked]: "openKasse",
    [GQL.IPendingBucketKind.AiDraftsToReview]: "openDrafts",
    [GQL.IPendingBucketKind.DraftsWithoutAccount]: "openDrafts",
};

/** The product of the largest bucket of a kind; drafts links open its record list. */
const largestProduct = (buckets: GQL.IPendingBucket[], kind: GQL.IPendingBucketKind) => {
    let best: GQL.IPendingBucket | undefined;
    for (const b of buckets) {
        if (b.kind === kind && b.product && b.count > (best?.count ?? 0)) {
            best = b;
        }
    }
    return best?.product ?? undefined;
};

const pendingTodos = (buckets: GQL.IPendingBucket[]): AgentTodoItem[] => {
    const totals = sumByKind(buckets);
    return Object.entries(PENDING_KINDS).flatMap(([key, textId]): AgentTodoItem[] => {
        const kind = key as GQL.IPendingBucketKind;
        const count = totals.get(kind) ?? 0;
        if (count <= 0 || !textId) {
            return [];
        }
        const action = BUCKET_ACTIONS[kind];
        const product = action === "openDrafts" ? largestProduct(buckets, kind) : undefined;
        return [
            { line: { textId, values: { count } }, ...(action ? { action } : {}), ...(product ? { product } : {}) },
        ];
    });
};

const bucketChanges = (before: GQL.IPendingBucket[] | undefined, after: GQL.IPendingBucket[] | null) => {
    // Without both reads a before/after figure would be invented.
    if (!before || !after) {
        return [];
    }
    const start = sumByKind(before);
    const end = sumByKind(after);
    return Object.values(GQL.IPendingBucketKind).map((kind): AgentBucketChange => ({
        kind,
        before: start.get(kind) ?? 0,
        after: end.get(kind) ?? 0,
    }));
};

const STEP_ACTIONS: Partial<Record<AgentStepId, AgentTodoAction>> = {
    upload: "openDocuments",
    aiReading: "openDocuments",
    aiRetry: "openDocuments",
    autoSort: "openDocuments",
    sortQuestions: "openDocuments",
    documentContacts: "openDocuments",
    kasseFromDocuments: "openKasse",
    draftsFromDocuments: "openDocuments",
    bankImport: "openBank",
    bankContacts: "openBank",
    draftsFromTransactions: "openBank",
    fillBank: "openBank",
    aiCheck: "openDrafts",
    postingRules: "openRules",
};

const RUN_ORDER: AgentStepId[] = AGENT_PHASES.flatMap((p) => p.steps);

/** Steps that never run again: start would ask the opening questions again and replace nothing useful. */
const NO_RERUN: ReadonlySet<AgentStepId> = new Set<AgentStepId>(["start", "report"]);

/** Whether a step can run again now: retry after a failure, rerun after a skip by the user or a met dependency. */
const rerunAction = (
    id: AgentStepId,
    report: AgentStepReport,
    steps: AgentContext["runReport"]["steps"],
): "retry" | "rerun" | undefined => {
    if (NO_RERUN.has(id)) {
        return undefined;
    }
    if (report.status === "failed") {
        return "retry";
    }
    if (report.status !== "skipped") {
        return undefined;
    }
    if (report.byUser === true || report.skipReason === "byUser") {
        return "rerun";
    }
    const waitsFor = report.skipReason === "dependency" ? (report.waitsFor ?? []) : [];
    const met = waitsFor.every((dep) => {
        const status = steps[dep]?.status;
        return status === "done" || status === "nothing";
    });
    return waitsFor.length > 0 && met ? "rerun" : undefined;
};

const stepLine = (id: AgentStepId, textId: ReportLine["textId"]): ReportLine => ({
    textId,
    labels: { step: `app.agent.step.${id}` },
});

const doneDetails = (report: AgentStepReport | undefined): number =>
    report?.details.filter((d) => d.group === "done").length ?? 0;

/**
 * The UStVA period the agent checks: the latest period whose due date (the 10th after the period, plus one month with
 * Dauerfristverlaengerung) has passed. A period not yet due is not reported; a future year checks nothing.
 */
const ustvaPeriod = (
    frequency: GQL.IUstvaFilingFrequency,
    year: number,
    today: Date,
    extensionMonths: number,
): number | null => {
    const quarterly = frequency === GQL.IUstvaFilingFrequency.Quarterly;
    const perYear = quarterly ? 4 : 12;
    const day = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    for (let n = perYear; n >= 1; n--) {
        const endMonth = quarterly ? n * 3 : n;
        if (day > new Date(year, endMonth + extensionMonths, 10)) return quarterly ? 40 + n : n;
    }
    return null;
};

const ustvaLine = async (ctx: AgentContext): Promise<ReportLine[]> => {
    const frequency = ctx.ustvaFrequency();
    if (!frequency || frequency === GQL.IUstvaFilingFrequency.None) return [];
    const period = ustvaPeriod(frequency, ctx.year, new Date(), ctx.ustvaDauerfrist() ? 1 : 0);
    if (period === null) return [];
    try {
        const exports = await ctx.readElsterExports();
        if (exports.some((e) => e.ericResultCode === 0 && filingCovers(e.period, period))) return [];
    } catch (error) {
        console.error("Agent report: elster exports read failed", error);
        return [];
    }
    const label = period > 40 ? `Q${period - 40}` : String(period).padStart(2, "0");
    return [{ textId: "app.agent.report.ustvaOpen", values: { period: `${label}/${ctx.year}` } }];
};

const draftProduct = (ctx: AgentContext): GQL.IProductKey | undefined => {
    const counts = new Map<GQL.IProductKey, number>();
    try {
        for (const id of ctx.created.recordIds) {
            const product = ctx.recordInfo(id)?.product;
            if (product) counts.set(product, (counts.get(product) ?? 0) + 1);
        }
    } catch (error) {
        // Without the run year's records there is no draft product; the report itself still comes.
        if (error instanceof AgentRecordsNotLoadedError) return undefined;
        throw error;
    }
    let best: GQL.IProductKey | undefined;
    for (const [product, count] of counts) if (count > (counts.get(best as GQL.IProductKey) ?? 0)) best = product;
    return best;
};

export const reportStep: AgentStep = {
    id: "report",
    async run(ctx, io) {
        const [buckets, ustva] = await withThinking(io, "app.agent.report.thinking", () =>
            Promise.all([
                ctx.readPendingBuckets().catch((error: unknown): null => {
                    console.error("Agent report: pendingWork read failed", error);
                    return null;
                }),
                ustvaLine(ctx),
            ]),
        );

        const results = RUN_ORDER.flatMap((id) => {
            const report = id === "report" ? undefined : ctx.runReport.steps[id];
            if (!report) {
                return [];
            }
            const action = rerunAction(id, report, ctx.runReport.steps);
            return [{ id, report, ...(action ? { action } : {}) }];
        });
        const done = results.flatMap(({ report }) => report.summary);
        const continuing = results.flatMap(({ id, report }): ReportLine[] =>
            report.continuesOnServer ? [stepLine(id, "app.agent.report.continuesInBinale")] : [],
        );
        const pending = buckets ? pendingTodos(buckets) : [];
        const open = [
            ...results.flatMap(({ report }) => report.open),
            ...continuing,
            ...pending.map((p) => p.line),
            ...ustva,
        ];

        const todo: AgentTodoItem[] = [
            ...results.flatMap(({ id, action }): AgentTodoItem[] =>
                action === "retry" ? [{ line: stepLine(id, "app.agent.report.todo.failed"), action, stepId: id }] : [],
            ),
            ...results.flatMap(({ id, report }) =>
                report.open.map((line): AgentTodoItem => {
                    const action = STEP_ACTIONS[id];
                    return { line, ...(action ? { action } : {}), stepId: id };
                }),
            ),
            ...continuing.map((line): AgentTodoItem => ({ line })),
            ...results.flatMap(({ id, action }): AgentTodoItem[] =>
                action === "rerun" ? [{ line: stepLine(id, "app.agent.report.todo.skipped"), action, stepId: id }] : [],
            ),
            ...pending,
            ...ustva.map((line): AgentTodoItem => ({ line })),
        ];

        const counts: Record<AgentStepStatus, number> = { done: 0, nothing: 0, skipped: 0, failed: 0 };
        for (const { report } of results) {
            counts[report.status] += 1;
        }
        const steps = ctx.runReport.steps;

        const report: AgentReportData = {
            year: ctx.year,
            done,
            open,
            draftProduct: draftProduct(ctx),
            mode: ctx.runReport.mode ?? "approval",
            durationMs: Math.max(0, Date.now() - ctx.runReport.startedAt),
            counts,
            buckets: bucketChanges(ctx.runReport.baseline, buckets),
            steps: results,
            todo,
            tiles: {
                sorted: doneDetails(steps.autoSort) + doneDetails(steps.sortQuestions),
                drafts: new Set(ctx.created.recordIds).size,
                bankImported: doneDetails(steps.bankImport),
                needsYou: todo.length,
            },
        };
        io.post({ kind: "report", textId: "app.agent.report.title", report });
        return { status: "done" };
    },
};
